<template>
  <div class="d-flex">
    <date-range-picker
      ref="picker"
      :opens="opens"
      :minDate="pickerMinDate"
      :maxDate="maxDate"
      :singleDatePicker="singleDatePicker"
      :timePicker="timePicker"
      :timePicker24Hour="timePicker24Hour"
      :showWeekNumbers="showWeekNumbers"
      :showDropdowns="showDropdowns"
      :autoApply="autoApply && !withTimezone"
      v-model="range"
      :linkedCalendars="linkedCalendars"
      class="cg-single-date-range-picker cg-date-range-picker"
      :class="{
        'cg-single-date-range-picker--disabled': disabled,
        'cg-single-date-range-picker--no-ranges': hideRanges,
        'cg-single-date-range-picker--top': showOnTop,
        'cg-single-date-range-picker--with-timezone': withTimezone,
      }"
      :style="pickerStyle"
      :ranges="hideRanges ? null : dateRanges"
      :locale-data="customOptions"
      :disabled="disabled"
      @update="updateValues"
      @select="({ startDate }) => pendingDate = startDate"
      @toggle="onToggle"
    >
      <template #input>
        <popper
          :disabled="!disabled || hideTooltip"
          :options="{
            placement: 'top',
            modifiers: { offset: { offset: '0, 4px' } }
          }"
        >
          <div class="popper px-4 py-2 text-center" style="max-width: 230px; width: 230px">
            To choose a future send date you must be a CG Elite user. Contact sales for more information.
          </div>
          <v-text-field
            v-model="rangeValue"
            :placeholder="placeholder"
            class="rounded-0 date-range-form thin-border"
            :style="{ maxWidth: withTimezone ? 'none' : '130px', minHeight: '36px', pointerEvents: 'auto' }"
            slot="reference"
            color="black"
            outlined
            readonly
            :disabled="disabled"
            solo
            flat
            dense
            hide-details
            tile
            :height="height"
          >
            <template #append>
              <icons-calendar />
            </template>
          </v-text-field>
        </popper>
      </template>

      <template #ranges="ranges">
        <div class="ranges">
          <ul>
            <li
              v-for="(range, name) in ranges.ranges" :key="name"
              @click="ranges.clickRange(range)"
              v-on:click="setRange(name)"
              :class="name === selectedRange ? 'active' : ''"
            >
              <span class="f14">{{name}}</span>
              <span
                class="f14 ml-1"
                v-if="name === 'This month' || name === 'Last month'">
                ({{getMonth(range[0].toDateString())}})
              </span>
            </li>
          </ul>
        </div>
      </template>

      <template #footer="{ clickApply, in_selection: inSelection }" v-if="showCheckbox || withTimezone">
        <div class="footer">
          <div v-if="withTimezone" class="footer__field">
            <label :for="`${_uid}-hour`">Select time:</label>
            <div class="d-flex align-center footer__time">
              <v-select
                v-model="pendingHour"
                :id="`${_uid}-hour`"
                :items="hourItems"
                attach
                outlined
                dense
                hide-details
                color="black"
                class="rounded-0"
              />
              <span class="mx-1">:</span>
              <v-select
                v-model="pendingMinute"
                :items="minuteItems"
                attach
                outlined
                dense
                hide-details
                color="black"
                class="rounded-0"
              />
            </div>
          </div>
          <div v-if="withTimezone" class="footer__field">
            <label :for="`${_uid}-timezone`">Select timezone:</label>
            <v-select
              v-model="pendingTimezone"
              :id="`${_uid}-timezone`"
              :items="timezones"
              attach
              outlined
              dense
              hide-details
              color="black"
              class="rounded-0"
            />
          </div>
          <div class="footer__actions">
            <common-checkbox v-if="showCheckbox" v-model="checkboxModel" center-content>
              Apply for all recipients
            </common-checkbox>
            <common-button
              v-if="withTimezone"
              height="32"
              class="ml-auto"
              :disabled="inSelection || isPendingTimePast"
              @click="clickApply"
            >
              Apply
            </common-button>
          </div>
        </div>
      </template>
    </date-range-picker>
  </div>
</template>

<script>
import Popper from 'vue-popperjs'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { DEFAULT_TIMEZONE, TIMEZONES } from '@/constants/timezones'
import { formatSendDate, formatWallTime, toWallTimeDate } from '@/utils/timezone'

export default {
  name: 'SingleDateRangePicker',
  components: {
    DateRangePicker,
    Popper,
  },
  props: {
    value: {
      type: String
    },
    setMinDateWeekLater: {
      type: Boolean,
      required: false,
      default: false
    },
    maxDate: {
      type: Date,
      required: false,
      default: new Date('01/01/2900')
    },
    allowPastDates: {
      type: Boolean,
      required: false,
      default: false
    },
    pickerStyle: {
      type: String,
      required: false,
      default: null
    },
    showCheckbox: {
      type: Boolean,
      required: false,
      default: false
    },
    checkboxValue: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Shipping date'
    },
    height: {
      type: [String, Number],
      required: false,
      default: 36
    },
    showRemoveItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideRanges: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Picks time and timezone too; the value is then wall time in that timezone (use with .sync:timezone)
    withTimezone: {
      type: Boolean,
      required: false,
      default: false,
    },
    timezone: {
      type: String,
      required: false,
      default: null,
    },
  },
  data () {
    let today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date()
    tomorrow.setDate(today.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    const nextWeek = new Date()
    nextWeek.setDate(today.getDate() + 7)
    nextWeek.setHours(0, 0, 0, 0)
    const minDate = new Date()
    minDate.setDate(minDate.getDate())
    today = new Date()
    if (this.setMinDateWeekLater) {
      minDate.setDate(minDate.getDate() + 7)
    }
    const dateRanges = {}
    if (!this.setMinDateWeekLater) {
      dateRanges.Today = [today, today]
      dateRanges.Tomorrow = [tomorrow, tomorrow]
    }
    dateRanges['Next week'] = [nextWeek, nextWeek]

    dateRanges[this.showRemoveItem ? 'Remove' : ''] = ['', '']

    return ({
      checked: false,
      opens: 'left',
      minDate: minDate,
      singleDatePicker: true,
      timePicker: false,
      timePicker24Hour: false,
      showWeekNumbers: false,
      showDropdowns: false,
      autoApply: true,
      linkedCalendars: true,
      range: {
        start: '',
        end: ''
      },
      dateRanges,
      customOptions: {
        direction: 'ltr',
        format: 'mm/dd/yyyy',
        separator: ' - ',
        applyLabel: 'Apply',
        cancelLabel: 'Cancel',
        weekLabel: 'W',
        customRangeLabel: 'Custom Range',
        daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        firstDay: 0
      },
      selectedRange: '',
      showOnTop: false,
      timezones: TIMEZONES,
      pendingTimezone: this.timezone || DEFAULT_TIMEZONE,
      openedAt: new Date(),
      pendingDate: null,
      pendingHour: 0,
      pendingMinute: 0,
    })
  },
  computed: {
    rangeValue: {
      cache: false,
      get () {
        const { startDate } = this.range
        if (!startDate) return null
        if (this.withTimezone) return this.value ? formatSendDate(this.value, this.selectedTimezone) : null
        const filteredDate = new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(startDate))
        return filteredDate
      },
      set (val) {
        if (!val) {
          this.$emit('input', '')
        } else if (val) {
          this.$emit('input', val)
          this.$emit('change', val)
        }
      }
    },
    // the calendar shows wall time in the picked timezone, so "now" has to be wall time there too
    pickerMinDate () {
      if (this.allowPastDates) return null
      if (!this.withTimezone) return this.minDate
      return toWallTimeDate(this.openedAt.toISOString(), this.pendingTimezone)
    },
    // hours and minutes already gone in the picked timezone can't be chosen for its today
    isPendingDateMinDay () {
      const { pickerMinDate, pendingDate } = this
      return !!pickerMinDate && !!pendingDate && pendingDate.toDateString() === pickerMinDate.toDateString()
    },
    hourItems () {
      return [...Array(24).keys()].map((hour) => ({
        text: String(hour).padStart(2, '0'),
        value: hour,
        disabled: this.isPendingDateMinDay && hour < this.pickerMinDate.getHours(),
      }))
    },
    minuteItems () {
      const isMinHour = this.isPendingDateMinDay && this.pendingHour === this.pickerMinDate.getHours()
      return [...Array(60).keys()].map((minute) => ({
        text: String(minute).padStart(2, '0'),
        value: minute,
        disabled: isMinHour && minute < this.pickerMinDate.getMinutes(),
      }))
    },
    isPendingTimePast () {
      const { pickerMinDate, pendingDate, pendingHour, pendingMinute } = this
      if (!pickerMinDate || !pendingDate) return false

      const pendingDateTime = new Date(pendingDate)
      pendingDateTime.setHours(pendingHour, pendingMinute, 59, 999)
      return pendingDateTime < pickerMinDate
    },
    selectedTimezone () {
      return this.timezone || DEFAULT_TIMEZONE
    },
    checkboxModel: {
      get () {
        return this.checkboxValue
      },
      set (val) {
        this.$emit('update:checkboxValue', val)
      }
    }
  },
  watch: {
    timezone () {
      if (this.withTimezone) this.syncRange(this.value)
    },
    value: {
      handler: function (model) {
        this.syncRange(model)
      },
      immediate: true
    }
  },
  methods: {
    syncRange (model) {
      if (!model) return
      const newDate = this.withTimezone ? toWallTimeDate(model, this.selectedTimezone) : new Date(model)
      this.range = {
        startDate: newDate,
        endDate: newDate
      }
    },
    filterToDate (val) {
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep',
        'Oct', 'Nov', 'Dec'
      ]
      if (val === null) {
        return
      }
      const arr = String(val).split(' ')
      return ((months.findIndex(month => month === arr[1]) + 1) + '/' + arr[2] + '/' + arr[3])
    },
    getMonth (val) {
      const monthIdxs = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep',
        'Oct', 'Nov', 'Dec'
      ]
      const months = [
        'January', 'February', 'March', 'April', 'May',
        'June', 'July', 'August', 'September',
        'October', 'November', 'December'
      ]
      return months[monthIdxs.findIndex(idx => idx === String(val).split(' ')[1])]
    },
    setRange (name) {
      this.selectedRange = name
      // range shortcuts pick the date without the picker's select event
      if (this.withTimezone) this.pendingDate = this.dateRanges[name]?.[0] || null
    },
    updateValues (value) {
      let range
      if (value.startDate === null) {
        range = null
      } else if (this.withTimezone) {
        // the calendar shows wall time, so it is sent as wall time in the picked timezone
        const sendDate = new Date(value.startDate)
        sendDate.setHours(this.pendingHour, this.pendingMinute, 0, 0)

        this.$emit('update:timezone', this.pendingTimezone)
        range = formatWallTime(sendDate)
      } else {
        range = new Date(value.startDate).toISOString()
      }
      this.rangeValue = range
    },
    onToggle (val) {
      if (!val) { return }

      this.pendingTimezone = this.selectedTimezone
      this.openedAt = new Date()

      if (this.withTimezone) {
        const { startDate } = this.range
        this.pendingDate = startDate ? new Date(startDate) : null
        this.pendingHour = startDate ? startDate.getHours() : 9
        this.pendingMinute = startDate ? startDate.getMinutes() : 0
      }

      if (this.$refs.picker) {
        this.$nextTick(() => {
          const dateRangePicker = this.$refs.picker.$el.querySelector('.daterangepicker')

          const { bottom } = dateRangePicker.getBoundingClientRect();
          const windowHeight = window?.innerHeight ?? 1000;

          const restBottom  = windowHeight - bottom;
          this.showOnTop = restBottom < 0
        })
      }
    },
  }
}
</script>

<style lang="scss">
.cg-single-date-range-picker {
  --picker-margin-top: 7px;
  --min-width: 130px;
  min-width: var(--min-width) !important;
  cursor: pointer;

  .reportrange-text {
    width: 100% !important;
    height: 36px !important;
    cursor: inherit;

    .v-input {
      min-width: var(--min-width) !important;
      cursor: inherit;


      .v-input__slot,
      input {
        cursor: inherit !important;
      }

      &--is-disabled, input {
        color: rgba(0, 0, 0, 0.87) !important;
      }
    }

    .datepicker__append-outer {
      position: absolute !important;
      right: 0 !important;
      top: 50% !important;
      transform: translate(0, -50%) !important;
      display: flex !important;
      align-items: center !important;
      height: 100% !important;
    }
  }

  .vue-daterange-picker{
    position: unset !important;
  }

  .daterangepicker {
    margin-top: var(--picker-margin-top) !important;
    right: 0 !important;
  }

  &--top > .daterangepicker {
    bottom: 100%;
  }


  & .left .next {
    visibility: unset !important;
  }

  & .daterangepicker {
    min-width: 492px !important;
  }

  &--disabled {
    & .reportrange-text {
      cursor: default;
    }

    & .reportrange-text .v-input > .v-input__control > .v-input__slot {
      background: #F4F4F4 !important;
    }
  }

  &--no-ranges .daterangepicker {
    min-width: unset !important;

    & .ranges {
      display: none !important;
    }

    & .drp-calendar.left {
      border: none !important;
    }
  }

  &:not(.cg-single-date-range-picker--disabled) {
    & .reportrange-text .v-input > .v-input__control > .v-input__slot {
      background: #fff !important;
    }
  }
}

.date-range-form {
  max-width: 200px;

  .v-input__slot {
    padding-left: 12px !important;
    font-size: 14px;
  }
}

.cg-date-range-picker {
  .reportrange-text {
    padding: 0 !important;
    border: none !important;
    opacity: 1 !important;
  }

  .drp-calendar {
    max-width: 295px !important;
    width: 295px !important;
  }

  .left {
    border-right: none !important;
    padding: 10px 0 10px 15px !important;
  }

  .right {
    .prev {
      visibility: hidden !important;
    }
  }

  .calendar-table {
    td, th {
      min-width: 38px !important;
      height: 38px !important;
      font-size: 14px!important;
    }
    td.active {
      background: #198b85 !important;
    }
    .in-range {
      background: #dff3f2 !important;
    }
    .off {
      visibility: hidden !important;
    }
  }

  .daterangepicker {
    top: auto;
    min-width: 790px;

    &::before,
    &::after {
      display: none;
    }

    .ranges {
      ul {
        width: 195px !important;
        padding: 10px 13px 0 7px;
      }

      li {
        display: flex;
        align-items: center;
        height: 26px;
        border-radius: 13px;
        color: rgba(0,0,0,0.54);

        &:hover {
          background: #f6f6f6;
        }
      }

      li:last-child {
        position: absolute;
        left: 7px;
        bottom: 20px;
        color: #42B77A;
        line-height: 18px;

        &:hover {
          background: none;
        }

        &.active {
          background: none;
        }
      }

      .active {
        background: #ebebeb;
        color: black;
      }
    }

    .footer {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 8px;
      background: #f2f2f2 !important;
      font-family: 'Lato-Regular', sans-serif;
      font-size: 15px;
      line-height: 18px;
      color: #16181f;
    }
  }
}

.cg-single-date-range-picker--with-timezone {
  .footer__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    font-size: 14px;
  }

  .footer__time .v-select {
    position: relative;
    max-width: 88px;
  }

  .daterangepicker {
    width: 492px !important;
    max-width: 492px !important;
  }

  .footer__field .v-input__slot {
    background: #fff !important;
  }

  // long timezone names wrap instead of widening the picker
  .footer__field .v-select__selection,
  .footer__field .v-list-item__title {
    white-space: normal;
    line-height: 18px;
  }

  .footer__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;

    .common-checkbox {
      align-items: center;
      white-space: nowrap;
    }
  }
}

.multiple-date-range-picker .daterangepicker {
  min-width: 790px !important;
}

.date-filtered .v-input__control .v-input__slot {
  background: #DFF3F2 !important;
}

.calendar-icon {
  margin-top: 5px;
  margin-right: -23px;
  z-index: 1;
}
</style>
